import { useState, type FormEvent } from "react";
import { Plus, X } from "lucide-react";
import { useAuth } from "../lib/auth.js";
import {
  useAddPreference,
  usePreferences,
  useRemovePreference,
  useTopics,
  useUpdatePreferenceWeight,
} from "../lib/queries.js";
import { AppShell } from "../components/AppShell.js";
import { TopicPicker } from "../components/TopicPicker.js";
import { Button, Card, Field, FullPageSpinner } from "../components/ui.js";

export default function Preferences() {
  const { user } = useAuth();
  const uid = user!.id;
  const { data: topics } = useTopics();
  const { data: prefs, isLoading } = usePreferences(uid);
  const addPref = useAddPreference(uid);
  const removePref = useRemovePreference(uid);
  const updateWeight = useUpdatePreferenceWeight(uid);
  const [keyword, setKeyword] = useState("");

  if (isLoading || !topics || !prefs) {
    return (
      <AppShell>
        <FullPageSpinner label="Tercihler yükleniyor…" />
      </AppShell>
    );
  }

  const topicPrefs = prefs.filter((p) => p.topic_id);
  const keywordPrefs = prefs.filter((p) => p.keyword);
  const selectedTopicIds = new Set(topicPrefs.map((p) => p.topic_id!));

  function toggleTopic(topicId: string) {
    const existing = topicPrefs.find((p) => p.topic_id === topicId);
    if (existing) removePref.mutate(existing.id);
    else addPref.mutate({ topic_id: topicId, weight: 3 });
  }

  function addKeyword(e: FormEvent) {
    e.preventDefault();
    const k = keyword.trim();
    if (!k) return;
    const exists = keywordPrefs.some((p) => p.keyword?.toLowerCase() === k.toLowerCase());
    if (!exists) addPref.mutate({ keyword: k, weight: 3 });
    setKeyword("");
  }

  return (
    <AppShell>
      <header className="mb-8">
        <p className="eyebrow mb-2">Tercihler</p>
        <h1 className="font-serif text-3xl text-slate-50">Neleri takip ediyorsunuz?</h1>
        <p className="mt-2 text-sm text-slate-400">
          Konularınızı ve anahtar kelimelerinizi yönetin. Ağırlık, bir konunun brifingdeki
          önceliğini belirler (1–5).
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Sol: aktif tercihler */}
        <div className="space-y-6 lg:col-span-2">
          <Card className="p-5">
            <h2 className="mb-4 font-serif text-lg text-slate-100">
              Takip edilen konular
              <span className="ml-2 text-sm text-slate-500">{topicPrefs.length}</span>
            </h2>
            {topicPrefs.length === 0 ? (
              <p className="text-sm text-slate-500">Henüz konu seçmediniz. Sağdan ekleyin.</p>
            ) : (
              <ul className="space-y-2">
                {topicPrefs.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-700/40 bg-ink-900/40 px-3 py-2"
                  >
                    <span className="text-sm text-slate-200">{p.topic?.label}</span>
                    <div className="flex items-center gap-2">
                      <select
                        value={p.weight}
                        onChange={(e) => updateWeight.mutate({ id: p.id, weight: Number(e.target.value) })}
                        className="rounded-md border border-slate-700/60 bg-ink-800 px-1.5 py-1 text-xs text-slate-300 focus:border-gold/50 focus:outline-none"
                        title="Ağırlık (öncelik)"
                      >
                        {[1, 2, 3, 4, 5].map((w) => (
                          <option key={w} value={w}>
                            ağırlık {w}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => removePref.mutate(p.id)}
                        className="rounded p-1 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
                        title="Kaldır"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <h2 className="mb-1 font-serif text-lg text-slate-100">Anahtar kelimeler</h2>
            <p className="mb-4 text-xs text-slate-500">
              Rakip şirket adı, ürün, kişi… Serbestçe ekleyin.
            </p>
            <form onSubmit={addKeyword} className="mb-3 flex items-end gap-2">
              <div className="flex-1">
                <Field
                  label=""
                  aria-label="Anahtar kelime"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="Örn. döviz kuru, rakip adı…"
                />
              </div>
              <Button type="submit" variant="outline" className="px-3">
                <Plus className="h-4 w-4" />
              </Button>
            </form>
            <div className="flex flex-wrap gap-2">
              {keywordPrefs.length === 0 ? (
                <span className="text-sm text-slate-500">Henüz anahtar kelime yok.</span>
              ) : (
                keywordPrefs.map((p) => (
                  <span
                    key={p.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-gold/10 py-1 pl-3 pr-1.5 text-sm text-gold-300"
                  >
                    {p.keyword}
                    <button
                      onClick={() => removePref.mutate(p.id)}
                      className="rounded-full p-0.5 hover:bg-gold/20"
                      title="Kaldır"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Sağ: konu ekleme */}
        <div className="lg:col-span-3">
          <Card className="p-5">
            <h2 className="mb-4 font-serif text-lg text-slate-100">Konu kataloğu</h2>
            <TopicPicker topics={topics} selected={selectedTopicIds} onToggle={toggleTopic} />
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
